import apiClient from '../api';

/**
 * Forms API Service
 * Endpoints para formulários dinâmicos do Form Builder
 */
export default {
  /**
   * Carrega o schema completo do formulário
   * @param {string} slug - Slug do formulário
   * @returns {Promise}
   */
  getFormSchema(slug) {
    return apiClient.get(`/forms/${slug}/schema`);
  },

  /**
   * Valida um campo individual (opcional - para validação assíncrona)
   * @param {string} slug - Slug do formulário
   * @param {object} data - { field_name, value, context }
   * @returns {Promise}
   */
  validateField(slug, data) {
    return apiClient.post(`/forms/${slug}/validate-field`, data);
  },

  /**
   * Submete o formulário completo
   * @param {string} slug - Slug do formulário
   * @param {FormData|object} formData - Dados do formulário
   * @returns {Promise}
   */
  submitForm(slug, formData) {
    const config = formData instanceof FormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 minutos para uploads
    } : {};
    return apiClient.post(`/forms/${slug}/submit`, formData, config);
  },

  /**
   * Identifica participante (step inicial de identificação)
   * @param {string} slug - Slug do formulário
   * @param {object} data - { email } ou { cpf }
   * @returns {Promise}
   */
  identifyParticipant(slug, data) {
    return apiClient.post(`/forms/${slug}/identify`, data);
  }
};

/**
 * Helper functions para uso nos componentes
 */

/**
 * Carrega schema do formulário e retorna apenas os dados
 * @param {string} slug - Slug do formulário
 * @returns {Promise<object>}
 */
export async function getFormSchema(slug) {
  const response = await apiClient.get(`/forms/${slug}/schema`);
  return response.data;
}

/**
 * Valida um campo individual
 * @param {string} slug - Slug do formulário
 * @param {string} fieldName - Nome do campo
 * @param {any} value - Valor do campo
 * @param {object} context - Contexto adicional (outros valores do form)
 * @returns {Promise<object>}
 */
export async function validateField(slug, fieldName, value, context = {}) {
  const response = await apiClient.post(`/forms/${slug}/validate-field`, {
    field_name: fieldName,
    value,
    context
  });
  return response.data;
}

/**
 * Submete o formulário
 * @param {string} slug - Slug do formulário
 * @param {FormData|object} formData - Dados do formulário
 * @returns {Promise<object>}
 */
export async function submitForm(slug, formData) {
  const config = formData instanceof FormData ? {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000 // 2 minutos para uploads
  } : {};
  const response = await apiClient.post(`/forms/${slug}/submit`, formData, config);
  return response.data;
}

/**
 * Identifica participante (legado - com slug)
 * @param {string} slug - Slug do formulário
 * @param {object} data - { email } ou { cpf }
 * @returns {Promise<object>}
 */
export async function identifyParticipant(slug, data) {
  const response = await apiClient.post(`/forms/${slug}/identify`, data);
  return response.data;
}

// ============================================
// NOVOS ENDPOINTS (sem slug - detecta automaticamente)
// ============================================

/**
 * Carrega schema do formulário global (para inscrições abertas)
 * @param {number|null} formId - ID do formulário (opcional, usado com Registration Control)
 * @param {number|null} rcItemId - ID do RC item identificado (opcional). Quando
 *   enviado, o backend resolve as tags do item e remove do schema os campos
 *   restritos por tag que não se aplicam a este participante.
 * @returns {Promise<object>}
 */
export async function getGlobalFormSchema(formId = null, rcItemId = null) {
  const params = {};
  if (formId) params.form_id = formId;
  if (rcItemId) params.registration_control_item_id = rcItemId;
  const response = await apiClient.get('/form/schema', { params });
  return response.data;
}

/**
 * Identifica participante e retorna formulário do grupo dele
 * Usado para eventos com lista de convidados importada
 * @param {object} data - { email } ou { cpf }
 * @returns {Promise<object>}
 */
export async function identifyAndGetForm(data) {
  const response = await apiClient.post('/form/identify', data);
  return response.data;
}

/**
 * Verifica se participante existe pelo email e retorna estado de pagamento.
 * Usado no step de identificação para inscrições abertas com pagamento.
 * @param {string} email - Email do participante
 * @returns {Promise<object>}
 */
export async function checkParticipantByEmail(email) {
  const response = await apiClient.post('/form/check-participant', { email });
  return response.data;
}

/**
 * Valida um campo (usa form_id)
 * @param {number} formId - ID do formulário
 * @param {string} fieldName - Nome do campo
 * @param {any} value - Valor do campo
 * @param {object} context - Contexto adicional
 * @returns {Promise<object>}
 */
export async function validateFieldGlobal(formId, fieldName, value, context = {}) {
  const response = await apiClient.post('/form/validate-field', {
    form_id: formId,
    field_name: fieldName,
    value,
    context
  });
  return response.data;
}

/**
 * Submete formulário (usa form_id)
 * @param {number} formId - ID do formulário
 * @param {FormData|object} formData - Dados do formulário
 * @param {number|null} participantId - ID do participante (se for atualização)
 * @param {number|null} groupId - ID do grupo (para inscrições abertas)
 * @returns {Promise<object>}
 */
export async function submitFormGlobal(formId, formData, participantId = null, groupId = null) {
  // Se for FormData, adiciona os IDs
  if (formData instanceof FormData) {
    formData.append('form_id', formId);
    if (participantId) formData.append('participant_id', participantId);
    if (groupId) formData.append('group_id', groupId);

    const response = await apiClient.post('/form/submit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 minutos para uploads
    });
    return response.data;
  }

  // Se for objeto
  const payload = {
    ...formData,
    form_id: formId,
    ...(participantId && { participant_id: participantId }),
    ...(groupId && { group_id: groupId })
  };

  const response = await apiClient.post('/form/submit', payload);
  return response.data;
}
